import { useCoreServices } from '@bangle.io/context';
import {
  Breadcrumb,
  buttonVariants,
  cn,
  Separator,
  Sidebar,
} from '@bangle.io/ui-components';
import { WsPath } from '@bangle.io/ws-path';
import { useAtomValue } from 'jotai';
import { Home } from 'lucide-react';
import React from 'react';
import { NoteBreadcrumb } from '../components/navigation/note-breadcrumb';
import { WsNameBreadcrumb } from '../components/navigation/ws-name-breadcrumb';
import { CloudToolbar } from '../share/cloud-toolbar';
import { useCloudWorkspaceAccess } from '../share/use-cloud-workspace-access';

export interface AppHeaderProps {
  children?: React.ReactNode;
}

/** The main application header container, holding the toolbar content and optional children. */
export function AppHeader({ children }: AppHeaderProps) {
  const coreServices = useCoreServices();
  const wsPaths = useAtomValue(coreServices.workspaceState.$noteWsPaths);
  const currentWsPath = useAtomValue(
    coreServices.workspaceState.$currentWsPath,
  );
  const currentWsName = useAtomValue(
    coreServices.workspaceState.$currentWsName,
  );
  const { isViewer } = useCloudWorkspaceAccess();

  const showEditorToolbar = Boolean(currentWsPath);

  return (
    <header className="desktop-titlebar-drag desktop-titlebar-surface flex h-[var(--bangle-app-titlebar-height)] max-h-[var(--bangle-app-titlebar-height)] min-h-[var(--bangle-app-titlebar-height)] shrink-0 items-center gap-2 overflow-hidden px-3">
      <div
        aria-hidden="true"
        className="desktop-titlebar-main-spacer shrink-0"
      />
      <div className="flex h-full min-w-0 flex-1 items-center gap-2">
        <ToolbarLeftSection
          showEditorToolbar={showEditorToolbar}
          currentWsPath={currentWsPath?.wsPath}
          currentWsName={currentWsName}
          readOnly={isViewer}
          wsPaths={wsPaths.map((wsPath) => wsPath.wsPath)}
        />
        {/* Note actions + account chrome stay in one right-side cluster so the
            breadcrumb can shrink without clipping save / star / email. */}
        <CloudToolbar />
      </div>
      {children ? (
        <div className="desktop-titlebar-no-drag">{children}</div>
      ) : null}
    </header>
  );
}

interface ToolbarLeftSectionProps {
  showEditorToolbar: boolean;
  currentWsPath: string | undefined;
  currentWsName: string | undefined;
  readOnly: boolean;
  wsPaths: string[];
}

function ToolbarLeftSection({
  showEditorToolbar,
  currentWsPath,
  currentWsName,
  readOnly,
  wsPaths,
}: ToolbarLeftSectionProps) {
  const coreServices = useCoreServices();

  return (
    <div className="desktop-titlebar-no-drag flex min-w-0 flex-1 items-center gap-2">
      <Sidebar.SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="h-4" />
      {showEditorToolbar && currentWsPath ? (
        <NoteBreadcrumb
          wsPath={currentWsPath}
          wsPaths={wsPaths}
          onNewNote={({ wsPath }) => {
            if (readOnly) {
              return;
            }
            const parent = WsPath.fromString(wsPath).getParent();
            const path = parent?.path;

            coreServices.commandDispatcher.dispatch(
              'command::ui:create-note-dialog',
              {
                prefillName: path || '',
              },
              'AppHeader',
            );
          }}
        />
      ) : currentWsName ? (
        <WsNameBreadcrumb wsName={currentWsName} />
      ) : (
        <HomeBreadcrumb coreServices={coreServices} />
      )}
    </div>
  );
}

function HomeBreadcrumb({
  coreServices,
}: {
  coreServices: ReturnType<typeof useCoreServices>;
}) {
  return (
    <Breadcrumb.Breadcrumb>
      <Breadcrumb.BreadcrumbList>
        <Breadcrumb.BreadcrumbItem>
          <Breadcrumb.BreadcrumbLink
            className={cn(
              buttonVariants({ variant: 'ghost', size: 'icon' }),
              'h-7 w-7',
            )}
            href={coreServices.navigation.toUri({
              route: 'welcome',
              payload: {},
            })}
            title={t.app.common.home}
          >
            <Home size={16} />
          </Breadcrumb.BreadcrumbLink>
        </Breadcrumb.BreadcrumbItem>
      </Breadcrumb.BreadcrumbList>
    </Breadcrumb.Breadcrumb>
  );
}
